/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useDispatch, useSelector } from 'react-redux';
import { Input, Row, Tooltip } from 'antd';
import { translate } from '@components/i18n';
import { formatMoney } from '@utils/money';
import { transferOrderMoneySymbolSelector, transferOrderSelector, updateOrderTransferItem } from '@redux/slices/transfer-order/order';
import { OrderItem } from '@redux/slices/transfer-order/order-item';
import { selectedTransferCampaignsSelector } from '@redux/slices/transfer-campaign';
import { colors } from '@utils/constants';
import { CampaignIndicator, EmptyItemMessage } from '@pages/order/components/styled';
import { IS_ARG } from '@config/env';
import { useQuantityInput } from '@hooks/use-quantity-input';

function sort(a: string | number, b: string | number): number {
    if (a > b) {
        return 1;
    }
    if (a < b) {
        return -1;
    }
    return 0;
}

export const transferItemsColumns = [
    {
        title: translate('columns.item'),
        dataIndex: 'descricao',
        width: '15%',
        sorter: (a, b) => sort(a.descricao, b.descricao),
        render: (text, record: OrderItem.Response): React.ReactNode => {
            const selectedCampaigns = useSelector(selectedTransferCampaignsSelector);
            const color = colors[selectedCampaigns.findIndex((c) => c.id === record.campaign?.id)];

            return (
                <Row wrap={false}>
                    {record.campaign && (
                        <Tooltip title={`${record.campaign.nome}`}>
                            <CampaignIndicator color={color}>
                                R
                            </CampaignIndicator>
                        </Tooltip>
                    )}
                    {text}
                </Row>
            );
        },
    },
    {
        title: translate('columns.code'),
        dataIndex: 'numero',
        width: '8%',
        sorter: (a, b) => sort(a.numero, b.numero),
    },
    {
        title: translate('columns.qtd'),
        dataIndex: 'quantity',
        width: '10%',
        sorter: (a, b) => sort(a.quantity, b.quantity),
        render: (_, record): React.ReactNode => {
            const transferOrder = useSelector(transferOrderSelector);
            const dispatch = useDispatch();

            const updateValue = (value: number): void => {
                dispatch(updateOrderTransferItem({
                    id: record.id,
                    quantity: value,
                }));
            };

            const quantityInput = useQuantityInput(record.quantity, updateValue);

            if (IS_ARG && record.aprovacaoSanasa && !transferOrder.client?.autorizacaoSanasa) {
                return (
                    <EmptyItemMessage>
                        {translate("general.noSanasaAuthorization")}
                    </EmptyItemMessage>
                )
            }

            return (
                <span>
                    <Input
                        placeholder={'Ex: 0'}
                        disabled={record.disabled}
                        type="number"
                        min={record.min ? record.min : '0'}
                        max={record.max ? record.max : undefined}
                        {...quantityInput}
                    />
                </span>
            );
        },
    },
    {
        title: translate('columns.presentationQtd'),
        dataIndex: 'multiplo',
        width: '8%',
        sorter: (a, b) => sort(a.multiplo, b.multiplo),
        render: (text, record): React.ReactNode => {
            const transferOrder = useSelector(transferOrderSelector);
            if (IS_ARG && record.aprovacaoSanasa && !transferOrder.client?.autorizacaoSanasa) {
                return ""
            }
            return (
                <span>{text}</span>
            );
        },
    },
    {
        title: translate('columns.amountQtd'),
        dataIndex: 'totalQuantity',
        width: '8%',
        sorter: (a, b) => sort(a.totalQuantity, b.totalQuantity),
        render: (text, record): React.ReactNode => {
            const transferOrder = useSelector(transferOrderSelector);
            if (IS_ARG && record.aprovacaoSanasa && !transferOrder.client?.autorizacaoSanasa) {
                return ""
            }
            return (
                <span>{text}</span>
            );
        },
    },
    {
        title: translate('columns.price'),
        dataIndex: 'preco',
        width: '15%',
        sorter: (a, b) => sort(a.preco, b.preco),
        render: (_, record): React.ReactNode => {
            const transferOrder = useSelector(transferOrderSelector);
            const moneySymbol = useSelector(transferOrderMoneySymbolSelector)

            if (IS_ARG && record.aprovacaoSanasa && !transferOrder.client?.autorizacaoSanasa) {
                return ""
            }

            return (
                <span>
                    {formatMoney(moneySymbol, record.preco)}
                </span>
            );
        },
    },
    {
        title: translate('columns.subTotal'),
        dataIndex: 'totalValue',
        width: '12%',
        sorter: (a, b) => sort(a.totalValue, b.totalValue),
        render: (text, record): React.ReactNode => {
            const transferOrder = useSelector(transferOrderSelector);
            const moneySymbol = useSelector(transferOrderMoneySymbolSelector)

            if (IS_ARG && record.aprovacaoSanasa && !transferOrder.client?.autorizacaoSanasa) {
                return ""
            }

            return (
                <span
                    style={{
                        display: 'flex',
                        columnGap: '5px',
                        userSelect: 'none',
                    }}
                >
                    {formatMoney(moneySymbol, text || 0)}
                </span>
            );
        },
    },
];

export const filterItemsOptions = [
    { value: 'all', label: translate('filters.all') },
    { value: 'descricao', label: translate('filters.description') },
    { value: 'numero', label: translate('filters.code') },
];

export const exportTransferItemsColumns = (items: OrderItem.Response[]) => {
    return items.map((item) => {
        return {
            Item: item.descricao,
            Cod: item.numero,
            Quantity: item.quantity,
            QtdApres: item.multiplo,
            QtdTotal: item.totalQuantity,
            Preco: item.preco,
            Subtotal: item.totalValue,
        };
    });
};